import React, { useState } from "react";
import { motion } from "framer-motion";

import { color } from "@/lib/theme";

// Palette tokens, not colours — resolved through `color()` at each use site.
// The trace is brand furniture except where a step carries a judgement: the
// evidence gap warns, and the action it produces is the proof moment.
const COLOR = {
  regulation: "brand",
  requirement: "brand",
  control: "brand-deep",
  systems: "brand",
  evidence: "warn",
  owner: "brand",
  action: "gold",
};

const NODES = [
  { id: "regulation", state: "regulation", label: "Regulatory change", sub: "Updated transparency requirement", rel: null,
    fields: [["Record", "REG-DEMO-0024"], ["Source", "Verified import"], ["Status", "Reviewed"]],
    note: "A verified regulatory change is imported and reviewed — the starting point of the trace." },
  { id: "requirement", state: "requirement", label: "Affected requirement", sub: "Users must receive updated disclosure", rel: "affects",
    fields: [["Requirement", "REQ-DEMO-07"], ["Framework", "EU AI Act (demo)"], ["Change", "Disclosure scope"]],
    note: "Meira maps the change to the governance requirement it affects." },
  { id: "control", state: "control", label: "Mapped control", sub: "AI Transparency Control", rel: "mapped to",
    fields: [["Control", "CTRL-DEMO-TR-07"], ["Type", "Disclosure"], ["Owner line", "AI Governance"]],
    note: "The requirement is already mapped to an operating control." },
  { id: "systems", state: "systems", label: "Affected AI systems", sub: "Support Agent · HR Screening", rel: "applies to",
    fields: [["System A", "Customer Support Agent"], ["System B", "HR Screening Assistant"], ["Scope", "2 systems"]],
    note: "Meira identifies exactly which AI systems the control touches." },
  { id: "evidence", state: "evidence", label: "Evidence check", sub: "1 current · 1 outdated · 1 missing", rel: "requires",
    fields: [["Disclosure policy", "Current"], ["User notice", "Outdated"], ["Approval record", "Missing"]],
    note: "The evidence gap is surfaced — what exists, what is stale, what is missing." },
  { id: "owner", state: "owner", label: "Owner", sub: "AI Governance Lead", rel: "assigned to",
    fields: [["Owner", "AI Governance Lead"], ["Accountable", "Yes"], ["Notified", "Yes"]],
    note: "The accountable owner is identified — a person, not a checkbox." },
  { id: "action", state: "action", label: "Action", sub: "Update disclosure · collect approval", rel: "produces",
    fields: [["Action 1", "Update user disclosure"], ["Action 2", "Collect approval evidence"], ["Priority", "HIGH"]],
    note: "The right person receives a prioritized, evidence-linked action. Meira recommends and coordinates — humans decide." },
];

const byId = (id) => NODES.find((n) => n.id === id);

export default function RegulationTrace() {
  const [active, setActive] = useState("regulation");
  const node = byId(active);
  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-start">
      <div>
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-line bg-panel-raised/60 px-4 py-3">
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-content-faint">Source mode</span>
          <span className="rounded-full border border-valid/25 bg-valid/10 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-valid">Verified import</span>
          <span className="rounded-full border border-line bg-panel-raised px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-content-faint">Authoritative feeds · planned</span>
        </div>
        <ol className="relative flex flex-col">
          <span className="absolute left-[11px] top-2 h-[calc(100%-1rem)] w-px bg-gradient-to-b from-brand/50 via-line to-gold/50" />
          {NODES.map((n, i) => {
            const tone = COLOR[n.state];
            const isActive = n.id === active;
            return (
              <li key={n.id} className="relative">
                {n.rel && <div className="ml-8 pb-1 pt-1"><span className="font-mono text-[9px] uppercase tracking-[0.18em] text-content-faint">{n.rel}</span></div>}
                <motion.button
                  data-testid={`reg-node-${n.id}`}
                  onMouseEnter={() => setActive(n.id)}
                  onFocus={() => setActive(n.id)}
                  onClick={() => setActive(n.id)}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.6 }}
                  transition={{ duration: 0.5, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  aria-label={`${n.label}: ${n.sub}`}
                  className={`relative flex w-full items-center gap-4 rounded-md border px-4 py-3 text-left transition-colors duration-300 focus:outline-none ${isActive ? "border-line-bright bg-panel-raised" : "border-line bg-panel-raised/70 hover:border-brand/50"}`}
                >
                  <span className="relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border" style={{ borderColor: color(tone, 0.4), background: color("ink") }}>
                    <span className="h-2 w-2 rounded-full" style={{ background: color(tone), boxShadow: isActive ? `0 0 10px ${color(tone, 0.45)}` : `0 0 4px ${color(tone, 0.3)}` }} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-mono text-[9px] uppercase tracking-[0.2em] text-content-faint">{n.label}</span>
                    <span className="block truncate text-sm text-content">{n.sub}</span>
                  </span>
                </motion.button>
              </li>
            );
          })}
        </ol>
      </div>

      <motion.div key={active} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} className="relative overflow-hidden rounded-lg border border-line bg-panel-raised p-5 sm:p-7 lg:sticky lg:top-28">
        <span className="absolute left-0 top-0 h-full w-[3px]" style={{ background: color(COLOR[node.state]) }} />
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-content-faint">{node.label}</div>
        <h4 className="mt-2 font-display text-2xl font-medium text-content">{node.sub}</h4>
        <p className="mt-4 text-sm leading-relaxed text-content-muted">{node.note}</p>
        <div className="mt-6 grid gap-px overflow-hidden rounded-md border border-line bg-panel-raised sm:grid-cols-2">
          {node.fields.map(([k, v]) => (
            <div key={k} className="bg-ink p-3">
              <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-content-faint">{k}</div>
              <div className="mt-1 font-mono text-[12px] text-content">{v}</div>
            </div>
          ))}
        </div>
        <p className="mt-5 font-mono text-[9px] uppercase tracking-[0.16em] text-content-faint">Synthetic data · tap a step to trace the impact</p>
      </motion.div>
    </div>
  );
}
